import {
  Activity,
  ActivityFormData,
  ProcessFlowHeadSettings,
  ProcessFlowSettings,
  defaultProcessFlowHeadSettings,
  defaultProcessFlowSettings,
} from "../models/Activity";
import { Field as FieldModel } from "../models/Field";
import { NumFieldType } from "../models/FieldTypes";
import { QueryFilter } from "../models/QueryFilter";
import { FlatVsaaTag } from "../models/VsaaTag";
import { ProcessFieldUniqueName } from "../constants/Fields";

interface ActivityFormValidationOptions {
  formData: ActivityFormData;
  activityFields?: FieldModel[];
}

export const searchActivities = (query: string, activities: Activity[]) => {
  const normalizedQuery = query.toLowerCase();

  const foundActivities: Activity[] = activities.filter((a) =>
    queryFoundInActivity(normalizedQuery, a)
  );

  return foundActivities;
};

const queryFoundInActivity = (normalizedQuery: string, activity: Activity) => {
  if (propValueContainsQuery(normalizedQuery, activity.title)) {
    return true;
  }

  return false;
};

const propValueContainsQuery = (normalizedQuery: string, propValue: string) => {
  return propValue.toLowerCase().indexOf(normalizedQuery) !== -1;
};

export const getProcessPathsOfActivity = (
  activity: Activity | undefined
): string[] => {
  return getProcessTagsOfActivity(activity).map((tag) => tag.path || "");
};

export const getProcessTagsOfActivity = (
  activity: Activity | undefined
): FlatVsaaTag[] => {
  const tags = activity?.fieldValues?.tags.process as FlatVsaaTag[];

  if (!tags || tags.length === 0) {
    return [];
  }

  return tags.sort((a, b) => a.position! - b.position!);
};

export const hasProcessFlowSubactivities = (
  formData: Pick<
    ActivityFormData,
    "fieldsWithValues" | "processesIdsWithSubactivities"
  >
): boolean => {
  const processTags = (formData.fieldsWithValues?.[ProcessFieldUniqueName] ??
    []) as FlatVsaaTag[];
  const processIdsWithSubactivities =
    formData.processesIdsWithSubactivities ?? [];

  return processTags.some((tag) =>
    processIdsWithSubactivities.includes(tag.id)
  );
};

export const getProcessIdsWithSubactivities = (
  processTags: FlatVsaaTag[],
  allProcessTags: FlatVsaaTag[],
  activityProcessIds: string[]
): string[] => {
  const activityProcessIdsSet = new Set(activityProcessIds);

  return processTags
    .filter((processTag) =>
      allProcessTags.some(
        (tag) =>
          tag.parentId === processTag.id && activityProcessIdsSet.has(tag.id)
      )
    )
    .map((tag) => tag.id);
};

export const canConfigureProcessFlowForActivity = (
  formData: Pick<
    ActivityFormData,
    "fieldsWithValues" | "processesIdsWithSubactivities"
  >
): boolean => {
  const processTags = (formData.fieldsWithValues?.[ProcessFieldUniqueName] ??
    []) as FlatVsaaTag[];

  return processTags.length > 0;
};

export const getProcessFlowSettingsForRequest = (
  formData: ActivityFormData
): ProcessFlowSettings | undefined => {
  if (!canConfigureProcessFlowForActivity(formData)) {
    return undefined;
  }

  return formData.processFlowSettings ?? undefined;
};

export const shouldInheritProcessFlowSettingsForRequest = (
  formData: ActivityFormData
): boolean =>
  canConfigureProcessFlowForActivity(formData) &&
  !hasProcessFlowOverride(formData.processFlowSettings);

export const getProcessFlowHeadSettingsForRequest = (
  formData: ActivityFormData
): ProcessFlowHeadSettings | undefined =>
  canConfigureProcessFlowForActivity(formData)
    ? (formData.processFlowHeadSettings ?? undefined)
    : undefined;

export const getProcessFlowHeadSettings = (
  processFlowHeadSettings?: ProcessFlowHeadSettings | null
): ProcessFlowHeadSettings => ({
  ...defaultProcessFlowHeadSettings,
  ...processFlowHeadSettings,
});

export const getPublishedToolFiltersForActivity = (
  activity: Activity | undefined
): QueryFilter[] => [
  {
    fieldUniqueName: "toolId",
    values:
      activity?.toolIds && activity.toolIds.length > 0
        ? activity.toolIds
        : ["none"],
  },
];

export const isActivityFormValid = ({
  formData,
  activityFields,
}: ActivityFormValidationOptions): boolean => {
  if (!formData.title) {
    return false;
  }

  if (!formData.richText && !formData.mirroredActivityId) {
    return false;
  }

  if (hasMissingRequiredActivityField(formData, activityFields)) {
    return false;
  }

  return true;
};

export const getProcessFlowSettings = (
  processFlowSettings?: ProcessFlowSettings | null
): ProcessFlowSettings => {
  const settings = {
    ...defaultProcessFlowSettings,
    ...processFlowSettings,
  };

  return {
    ...settings,
    activityShape: defaultProcessFlowSettings.activityShape,
  };
};

export const hasProcessFlowOverride = (
  processFlowSettings?: ProcessFlowSettings | null
): boolean => processFlowSettings != null;

const hasMissingRequiredActivityField = (
  formData: ActivityFormData,
  activityFields: FieldModel[] | undefined
): boolean => {
  return (
    activityFields?.some((field) => {
      if (!field.isRequired) {
        return false;
      }

      const value = formData.fieldsWithValues?.[field.uniqueName];

      if (field.type === NumFieldType.TagPicker) {
        return !Array.isArray(value) || value.length === 0;
      }

      return false;
    }) ?? false
  );
};

/**
 * Parses both image IDs and version IDs from HTML content.
 
 * @param html - The HTML content containing img tags
 * @returns Object with parallel arrays of imageIds and imageVersionIds
 */
export const parseImageDataFromHtml = (
  html: string
): { imageIds: string[]; imageVersionIds: string[] } => {
  if (!html) return { imageIds: [], imageVersionIds: [] };

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const imgElements = doc.querySelectorAll("img[data-image-id]");

  const imageIds: string[] = [];
  const imageVersionIds: string[] = [];

  imgElements.forEach((img) => {
    const imageId = img.getAttribute("data-image-id");
    const versionId = img.getAttribute("data-version-id");

    if (imageId) {
      imageIds.push(imageId);
      imageVersionIds.push(versionId || "");
    }
  });

  return { imageIds, imageVersionIds };
};

/**
 * Parses tool link IDs from HTML content.
 *
 * @param html - The HTML content containing anchor tags with data-tool-link-id
 * @returns Array of unique tool link IDs
 */
export const parseToolLinkIdsFromHtml = (html: string): string[] => {
  if (!html) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const linkElements = doc.querySelectorAll("a[data-tool-link-id]");

  const toolLinkIds: string[] = [];

  linkElements.forEach((link) => {
    const toolLinkId = link.getAttribute("data-tool-link-id");

    if (toolLinkId && !toolLinkIds.includes(toolLinkId)) {
      toolLinkIds.push(toolLinkId);
    }
  });

  return toolLinkIds;
};
